import dayjs from 'dayjs'
import numeral from 'numeral'

export const numeralFormat = (value: number, fmt: string) => {
	// PATCH: numeraljs issue that turns small and large numbers into NaN (https://github.com/adamwdraper/Numeral-js/issues/596)
	//        We need to deal with the small numbers, but the large numbers seem out of scope, so no need to check for them.
	const NUMERALJS_MIN_VALUE = 1e-7

	const _value =
		Math.abs(value) > NUMERALJS_MIN_VALUE ? value : value >= 0 ? NUMERALJS_MIN_VALUE * 10 : -(NUMERALJS_MIN_VALUE * 10)

	return numeral(_value).format(fmt)
}

export const multiply = (num: number | undefined | null, multiplier: number) => {
	if (typeof num === 'undefined' || num === null) return null
	return num * multiplier
}

export const divide = (num: number | undefined | null, multiplier: number) => {
	if (typeof num === 'undefined' || num === null) return null
	return num / multiplier
}

export const convertToValidFilename = (str: string, delimiter?: string) => str.replace(/[\/|\\:*?"<>]/g, delimiter ?? '')

export const get12from24Time = (hours: number, minutes?: number) => {
	let half = 'am'
	if (hours > 12) {
		half = 'pm'
		hours = hours % 12
	}

	if (hours === 0) hours = 12

	let minutes_string = ''

	if (typeof minutes != 'undefined') {
		minutes_string = ':' + minutes.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })
	}
	return `${hours}${minutes_string} ${half}`
}

export const getISOStringFromParts = (
	year_part: number,
	month_part: number,
	day_part: number,
	hour_part: number,
	minute_part: number,
) => {
	if ([year_part, month_part].some((part) => part == null)) return null

	return dayjs()
		.year(year_part)
		.month(month_part)
		.date(day_part || 0)
		.hour(hour_part || 0)
		.minute(minute_part || 0)
		.toISOString()
}

export const shortenIntersectionName = (s: string) => {
	const NAME_PART_DELIMITER = '/'
	const NAME_PART_MAX_LENGTH = 10

	return s
		.split(NAME_PART_DELIMITER)
		.map((name_part) =>
			name_part.length > NAME_PART_MAX_LENGTH ? `${name_part.substring(0, NAME_PART_MAX_LENGTH - 3)}...` : name_part,
		)
		.join(NAME_PART_DELIMITER)
}

export const splitAndShortenIntersectionName = (s: string) => {
	const NAME_PART_DELIMITER = '/'
	const NAME_PART_MAX_LENGTH = 15

	return s
		.split(NAME_PART_DELIMITER)
		.map((name_part, idx) => (idx === 1 ? `${NAME_PART_DELIMITER} ${name_part}` : name_part.trim()))
		.map((name_part) =>
			name_part.length > NAME_PART_MAX_LENGTH ? `${name_part.substring(0, NAME_PART_MAX_LENGTH - 3)}...` : name_part,
		)
}

const MONTH_ORDER = [
	'january',
	'february',
	'march',
	'april',
	'may',
	'june',
	'july',
	'august',
	'september',
	'october',
	'november',
	'december',
]
const MONTH_SHORT_LABELS = {
	january: 'Jan',
	february: 'Feb',
	march: 'Mar',
	april: 'Apr',
	may: 'May',
	june: 'Jun',
	july: 'Jul',
	august: 'Aug',
	september: 'Sep',
	october: 'Oct',
	november: 'Nov',
	december: 'Dec',
}

const monthsToShortLabel = (months) => {
	// Missing or fully-empty months means "All Year" (legacy default).
	if (!months) return 'All Year'
	const active_indexes = MONTH_ORDER.map((key, idx) => (months[key] ? idx : -1)).filter((idx) => idx !== -1)

	if (active_indexes.length === 0 || active_indexes.length === 12) return 'All Year'
	if (active_indexes.length === 1) return MONTH_SHORT_LABELS[MONTH_ORDER[active_indexes[0]]]

	const is_contiguous = active_indexes.every((idx, i) => i === 0 || idx === active_indexes[i - 1] + 1)
	if (is_contiguous) {
		const first = MONTH_SHORT_LABELS[MONTH_ORDER[active_indexes[0]]]
		const last = MONTH_SHORT_LABELS[MONTH_ORDER[active_indexes[active_indexes.length - 1]]]
		return `${first}-${last}`
	}
	return active_indexes.map((idx) => MONTH_SHORT_LABELS[MONTH_ORDER[idx]]).join(', ')
}

const DOM_ORDER = Array.from({ length: 31 }, (_, i) => String(i + 1))

const domsToShortLabel = (doms) => {
	// Missing or fully-empty doms means "All Days" (legacy default).
	if (!doms) return 'All Days'
	const active = DOM_ORDER.filter((k) => doms[k]).map(Number)
	if (active.length === 0 || active.length === 31) return 'All Days'
	if (active.length === 1) return `Day ${active[0]}`
	const is_contiguous = active.every((n, i) => i === 0 || n === active[i - 1] + 1)
	if (is_contiguous) return `Days ${active[0]}-${active[active.length - 1]}`
	return `Days ${active.join(', ')}`
}

// Day-of-week pattern label: "Daily" | "Weekdays" | "Weekends" | "MTuF" (optimus days object)
const daysToShortLabel = (days) => {
	if (!days) return ''
	if (days.monday && days.tuesday && days.wednesday && days.thursday && days.friday && days.saturday && days.sunday)
		return 'Daily'
	if (days.monday && days.tuesday && days.wednesday && days.thursday && days.friday && !days.saturday && !days.sunday)
		return 'Weekdays'
	if (
		!days.monday &&
		!days.tuesday &&
		!days.wednesday &&
		!days.thursday &&
		!days.friday &&
		days.saturday &&
		days.sunday
	)
		return 'Weekends'
	let label = ''
	if (days.monday) label += 'M'
	if (days.tuesday) label += 'Tu'
	if (days.wednesday) label += 'W'
	if (days.thursday) label += 'Th'
	if (days.friday) label += 'F'
	if (days.saturday) label += 'Sa'
	if (days.sunday) label += 'Su'
	return label
}

// Time range label: "6am - 10am" (drops :00 for whole hours)
const formatTimeRange = (local_start_time, local_end_time) => {
	let label = ''
	if (local_start_time) label += dayjs(local_start_time, 'HH:mm').format('h:mma').replace(':00', '')
	label += ' - '
	if (local_end_time) label += dayjs(local_end_time, 'HH:mm').format('h:mma').replace(':00', '')
	return label
}

// Long time range label: "10:00AM - 07:00PM"
const formatTimeRangeLong = (local_start_time: string, local_end_time: string) => {
	const start = local_start_time ? dayjs(local_start_time, 'HH:mm').format('hh:mmA') : ''
	const end = local_end_time ? dayjs(local_end_time, 'HH:mm').format('hh:mmA') : ''
	return `${start} - ${end}`
}

// Optimus display label: "{Name} {Time Range} ({Days of week}, {Months}, {Days of month})".
// `name` is the bare TOD name (e.g. "Weekday AM Peak"); omitted when empty.
export const formatTODTextWithName = (tod, name) => {
	const segments = [
		daysToShortLabel(tod.days),
		monthsToShortLabel(tod.months),
		domsToShortLabel(tod.days_of_month),
	].filter(Boolean)
	const prefix = name ? `${name} ` : ''
	const suffix = segments.length ? ` (${segments.join(', ')})` : ''
	return `${prefix}${formatTimeRangeLong(tod.local_start_time, tod.local_end_time)}${suffix}`
}

export const formatTODText = (tod) => {
	let name = ''

	// tod with name
	if (tod?.name) {

		name = tod?.name

		if (tod?.name !== 'All Periods' && tod?.local_start_time && tod?.local_end_time) {
			name += `: ${formatTimeRangeLong(tod.local_start_time, tod.local_end_time)}`
		}

		return name
	}

	// optimus tod
	if (tod.days) {
		name += daysToShortLabel(tod.days)

		const months_label = monthsToShortLabel(tod.months)
		const doms_label = domsToShortLabel(tod.days_of_month)
		const segments = [months_label, doms_label].filter(Boolean)
		if (segments.length) name += ` (${segments.join(', ')})`
		name += ' '
	}
	// v1 tod
	else {
		if (
			tod.has_monday &&
			tod.has_tuesday &&
			tod.has_wednesday &&
			tod.has_thursday &&
			tod.has_friday &&
			tod.has_saturday &&
			tod.has_sunday
		) {
			name += 'Daily '
		} else if (
			tod.has_monday &&
			tod.has_tuesday &&
			tod.has_wednesday &&
			tod.has_thursday &&
			tod.has_friday &&
			!tod.has_saturday &&
			!tod.has_sunday
		) {
			name += 'Weekdays '
		} else if (
			!tod.has_monday &&
			!tod.has_tuesday &&
			!tod.has_wednesday &&
			!tod.has_thursday &&
			!tod.has_friday &&
			tod.has_saturday &&
			tod.has_sunday
		) {
			name += 'Weekends '
		} else {
			if (tod.has_monday) name += 'M'
			if (tod.has_tuesday) name += 'Tu'
			if (tod.has_wednesday) name += 'W'
			if (tod.has_thursday) name += 'Th'
			if (tod.has_friday) name += 'F'
			if (tod.has_saturday) name += 'Sa'
			if (tod.has_sunday) name += 'Su'
			name += ' '
		}
	}



	name += formatTimeRangeLong(tod.local_start_time, tod.local_end_time)

	return name
}

export const formatDateRangeText = (date_range) => {
	const { start_date, end_date, exclude_holidays, excluded_dates } = date_range

	if (!start_date || !end_date) return ''

	let name = ''

	name += `${dayjs.utc(start_date).format('MM/DD/YYYY')}`
	name += ' -'
	name += ` ${dayjs.utc(end_date).subtract(1, 'day').format('MM/DD/YYYY')}` // Subtract 1 day because date range is exclusive of end_date

	if (exclude_holidays || excluded_dates?.length) {
		name += ' Excluding'
		if (exclude_holidays) {
			name += ' Holidays'
		}
		if (exclude_holidays && excluded_dates?.length) {
			name += ' and'
		}
		if (excluded_dates.length) {
			name += ` ${excluded_dates.map((d) => dayjs.utc(d).format('MM/DD/YYYY')).join(',')}`
		}
	}

	return name
}

export const getLevel = (before?: number, after?: number) => {
	if (!Number.isFinite(before) || !Number.isFinite(after)) return '-'
	const change = getChange(before, after)
	if (!Number.isFinite(change)) return '-'
	const level = Math.abs(change) > 0.5 ? 3 : 1

	if (change > 0) {
		return `+${level}`
	}
	if (change < 0) {
		return `-${level}`
	}

	return '-'
}

export const getChange = (before?: number, after?: number, round?: number) => {
	if (before === undefined || after === undefined) return '-'
	if (!Number.isFinite(after) || !Number.isFinite(before)) return '-'

	const change =
		(toFixedWithoutRounding(after, round) - toFixedWithoutRounding(before, round)) /
		toFixedWithoutRounding(before, round)

	if (!Number.isFinite(change)) return '-'

	return change
}

export const toFixedWithoutRounding = (num: number, round = 1) => {
	return parseFloat(num.toFixed(round))
}

export const formatFloat = (num: number) =>
	Number.isFinite(num) ? '0.' + '0'.repeat((num.toString().split('.')[1] || '').length) : 'Invalid number'
